import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Trash, 
  GitBranch,
  ArrowRight
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';

// Natural language operators for conversation flow
const NATURAL_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'matches_regex', label: 'matches pattern' }
];

// Auto-created step tracking (single source of truth)
const autoCreatedSteps = new Map(); // conditionId -> stepId

const ConditionEditor = ({ 
  condition, 
  onUpdate, 
  onDelete, 
  availableSteps = [],
  promptConfig = {},
  onAddStep,
  onRemoveStep,
  parentStepId,
  index = 0,
  className = ""
}) => {
  const [localCondition, setLocalCondition] = useState({
    id: '',
    name: `Branch ${index + 1}`,
    condition_operator: 'equals',
    expected_value: '',
    target_step_id: '',
    is_active: true,
    ...condition
  });

  // Track if this condition has an auto-created step
  const conditionId = localCondition.id || `condition-${index}`;
  const autoCreatedStepRef = useRef(null);

  useEffect(() => {
    setLocalCondition({
      id: '',
      name: `Branch ${index + 1}`,
      condition_operator: 'equals',
      expected_value: '',
      target_step_id: '',
      is_active: true,
      ...condition
    });
  }, [condition, index]);

  // **ATOMIC STEP MANAGEMENT** - Core of the solution
  const handleAtomicStepSwitch = async (newStepType) => {
    try {
      // 1. CLEANUP PHASE: Remove old auto-created step if it exists
      const currentAutoStepId = autoCreatedSteps.get(conditionId);
      if (currentAutoStepId && onRemoveStep) {
        // Verify it's not referenced elsewhere before cleanup
        const isOrphaned = !availableSteps.some(step => 
          step.id !== currentAutoStepId && 
          (step.conditions?.some(cond => cond.target_step_id === currentAutoStepId) ||
           step.target_step_id === currentAutoStepId)
        );

        if (isOrphaned) {
          await onRemoveStep(currentAutoStepId);
        }

        // Clean up tracking
        autoCreatedSteps.delete(conditionId);
        autoCreatedStepRef.current = null;
      }

      // 2. CREATION PHASE: Create new step atomically
      if (onAddStep) {
        const newStepId = await onAddStep(newStepType, parentStepId);

        if (newStepId) {
          // 3. UPDATE PHASE: Update condition and tracking atomically
          autoCreatedSteps.set(conditionId, newStepId);
          autoCreatedStepRef.current = newStepId;

          const updatedCondition = { 
            ...localCondition, 
            target_step_id: newStepId,
            variable_path: 'user_input'
          };

          setLocalCondition(updatedCondition);

          if (onUpdate) {
            onUpdate(updatedCondition);
          }

          return newStepId;
        }
      }
    } catch (error) {
      console.error('Atomic step switch failed:', error);
      // Rollback on failure - maintain consistency
      if (autoCreatedStepRef.current) {
        autoCreatedSteps.set(conditionId, autoCreatedStepRef.current);
      }
    }

    return null;
  };

  const handleFieldChange = (field, value) => {
    // **ATOMIC OPERATION**: Handle "Add New Step" selections
    if (field === 'target_step_id' && value.startsWith('add_new_')) {
      const stepType = value.replace('add_new_', '');
      handleAtomicStepSwitch(stepType);
      return;
    }

    // **CLEANUP CHECK**: If user manually selects existing step, clean up auto-created
    if (field === 'target_step_id' && !value.startsWith('add_new_')) {
      const currentAutoStepId = autoCreatedSteps.get(conditionId);
      if (currentAutoStepId && currentAutoStepId !== value && onRemoveStep) {
        // Check if the auto-created step is orphaned
        const isOrphaned = !availableSteps.some(step => 
          step.id !== currentAutoStepId && 
          (step.conditions?.some(cond => cond.target_step_id === currentAutoStepId) ||
           step.target_step_id === currentAutoStepId)
        );

        if (isOrphaned) {
          onRemoveStep(currentAutoStepId);
        }

        // Clean up tracking
        autoCreatedSteps.delete(conditionId);
        autoCreatedStepRef.current = null;
      }
    }

    // Standard field update
    const updatedCondition = { 
      ...localCondition, 
      [field]: value,
      variable_path: 'user_input'
    };

    setLocalCondition(updatedCondition);

    if (onUpdate) {
      onUpdate(updatedCondition);
    }
  };

  // **CLEANUP ON UNMOUNT** - Prevent memory leaks
  useEffect(() => {
    return () => {
      const autoStepId = autoCreatedSteps.get(conditionId);
      if (autoStepId) {
        autoCreatedSteps.delete(conditionId);
      }
    };
  }, [conditionId]);

  const getStepName = (stepId) => {
    const step = availableSteps.find(s => s.id === stepId);
    return step ? step.name : 'Select step...';
  };

  const isMultipleChoice = promptConfig?.prompt_type === 'multiple_choice';
  const availableOptions = promptConfig?.options || [];

  return (
    <div className={`p-3 border border-orange-200 rounded-lg bg-white hover:bg-orange-50 ${className}`}>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Branch number */}
        <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
          {index + 1}
        </div>

        {/* "If user response" - fixed text */}
        <span className="text-sm text-gray-600 whitespace-nowrap">If user response</span>

        {/* Operator */}
        <Select
          value={localCondition.condition_operator}
          onValueChange={(value) => handleFieldChange('condition_operator', value)}
        >
          <SelectTrigger className="w-auto min-w-[80px] h-8 text-sm border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[10008]">
            {NATURAL_OPERATORS.map((operator) => (
              <SelectItem key={operator.value} value={operator.value}>
                {operator.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Expected Value - context aware */}
        <div className="flex-shrink-0">
          {isMultipleChoice ? (
            <Select
              value={localCondition.expected_value}
              onValueChange={(value) => handleFieldChange('expected_value', value)}
            >
              <SelectTrigger className="w-auto min-w-[120px] h-8 text-sm border-gray-300">
                <SelectValue placeholder="Choose option..." />
              </SelectTrigger>
              <SelectContent className="z-[10008]">
                {availableOptions.filter(option => option && option.trim() !== '').map((option, idx) => (
                  <SelectItem key={idx} value={option}>
                    {option}
                  </SelectItem>
                ))}
                <SelectItem value="_default_fallback">
                  <span className="italic text-gray-600">Any other response</span>
                </SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={localCondition.expected_value}
              onChange={(e) => handleFieldChange('expected_value', e.target.value)}
              placeholder="text to match..."
              className="w-32 h-8 text-sm border-gray-300"
            />
          )}
        </div>

        {/* Arrow */}
        <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />

        {/* Go to step */}
        <span className="text-sm text-gray-600 whitespace-nowrap">go to</span>

        {/* Target Step */}
        <Select
          value={localCondition.target_step_id}
          onValueChange={(value) => handleFieldChange('target_step_id', value)}
        >
          <SelectTrigger className="w-auto min-w-[120px] h-8 text-sm border-gray-300">
            <SelectValue placeholder="Select step..." />
          </SelectTrigger>
          <SelectContent className="z-[10008]">
            {availableSteps.map((step) => (
              <SelectItem key={step.id} value={step.id}>
                {step.name}
              </SelectItem>
            ))}
            {onAddStep && availableSteps.length > 0 && (
              <div className="border-t border-gray-200 my-1" />
            )}
            {onAddStep && (
              <>
                <SelectItem value="add_new_action" className="text-blue-600 font-medium">
                  <div className="flex items-center gap-2">
                    <Plus className="h-3 w-3" />
                    Add New Action Step
                  </div>
                </SelectItem>
                <SelectItem value="add_new_condition" className="text-orange-600 font-medium">
                  <div className="flex items-center gap-2">
                    <Plus className="h-3 w-3" />
                    Add New Condition Step
                  </div>
                </SelectItem>
                <SelectItem value="add_new_end" className="text-purple-600 font-medium">
                  <div className="flex items-center gap-2">
                    <Plus className="h-3 w-3" />
                    Add New End Step
                  </div>
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>

        {/* Delete button */}
        {onDelete && (
          <button
            onClick={onDelete}
            className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
            title="Delete branch"
          >
            <Trash className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Main container for managing multiple conditions (decision branches)
export const ConditionsList = ({ 
  conditions = [], 
  onAddCondition, 
  onUpdateCondition, 
  onDeleteCondition,
  availableSteps = [],
  promptConfig = {},
  onAddStep,
  onRemoveStep,
  parentStepId,
  className = ""
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Conditions List */}
      {conditions.length > 0 && (
        <div className="space-y-2">
          {conditions.map((condition, index) => (
            <ConditionEditor
              key={condition.id || index}
              condition={condition}
              index={index}
              onUpdate={(updatedCondition) => onUpdateCondition && onUpdateCondition(index, updatedCondition)}
              onDelete={() => onDeleteCondition && onDeleteCondition(index)}
              availableSteps={availableSteps}
              promptConfig={promptConfig}
              onAddStep={onAddStep}
              onRemoveStep={onRemoveStep}
              parentStepId={parentStepId}
              className=""
            />
          ))}
        </div>
      )}

      {/* Add Branch Button */}
      {conditions.length > 0 && (
        <div className="flex items-center justify-center pt-2">
          <button
            onClick={onAddCondition}
            className="flex items-center gap-2 px-4 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 border border-orange-200 hover:border-orange-300 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add branch
          </button>
        </div>
      )}

      {/* Empty state */}
      {conditions.length === 0 && (
        <div className="text-center py-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg">
          <GitBranch className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-3">No decision branches yet</p>
          <button
            onClick={onAddCondition}
            className="flex items-center gap-2 px-4 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 border border-orange-200 hover:border-orange-300 rounded-lg transition-colors mx-auto"
          >
            <Plus className="h-4 w-4" />
            Create first branch
          </button>
        </div>
      )}

      {/* Help text */}
      {conditions.length > 0 && (
        <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-2 mt-3">
          💡 Branches are checked in order. The first matching condition determines where the user goes next.
        </div>
      )}
    </div>
  );
};

export default ConditionEditor; 